import { Injectable, Renderer2 } from '@angular/core';
import { ApplicationService, AuthenticationService, ConfigService, LoggingService, PITEventService, RoutingService, SystemEnvironment, UtilsMisc } from '@pit/core';
import { IDtoUser } from '@pit/frontend-model';
import { MenuItem } from 'primeng/api';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { EventUI } from '../../../module.event/pin.it.track.frontend.events';
import { AppMode, IPinItTrackFrontendApplicationService } from '../interfaces/pin.it.track.frontend.application.service.interface';
import { IPinItTrackFrontendAuthenticationService } from '../interfaces/pin.it.track.frontend.authentication.service.interface';

@Injectable({
    providedIn: 'root'
})
export class PinItTrackFrontendApplicationService extends ApplicationService implements IPinItTrackFrontendApplicationService {

    protected _isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected isLoadingCount: number = 0;

    private breadcrumbItemsSource = new Subject<MenuItem[]>();
    private document: Document = null;
    private menuSource = new Subject<string>();
    private resetSource = new Subject<void>();
    private renderer: Renderer2 = null;

    constructor(configurationService: ConfigService, loggingService: LoggingService, routngService: RoutingService, eventService: PITEventService,
        private authenticationService: AuthenticationService) {
        super(configurationService, loggingService, routngService, eventService);

        this.headerCaption = 'PIN Track ADMIN';
        switch (this.configurationService.configSystem.environment) {
            case SystemEnvironment.Production: {
                this.headerCaption = this.headerCaption + ' - LIVE';
                break;
            }
            default: {
                this.headerCaption = this.headerCaption + ' - TEST';
                break;
            }
        }

        this.loggingService.debug('PinItTrackFrontendApplicationService --> constructor()');
    }

    public appMode: AppMode = AppMode.User;

    public get isLoading(): boolean {
        return this.isLoadingSubject.getValue();
    }

    public set isLoading(value: boolean) {
        if (value === true) {
            this.isLoadingCount = this.isLoadingCount + 1;
        } else if (this.isLoadingCount > 0) {
            this.isLoadingCount = this.isLoadingCount - 1;
        }
        if (this.isLoadingCount > 0) {
            this.isLoadingSubject.next(true);
        } else {
            this.isLoadingSubject.next(false);
        }
    }

    public readonly isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public readonly isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

    protected override async doInitializeAsync(): Promise<void> {
        await super.doInitializeAsync();

        if (this.isInitialized === false) {
            this.applicationTitle = 'PIN Track ADMIN';

            this.eventService.registerMethod('loading', (value: boolean) => {
                this.eventService.sendEvent('application', {
                    type: 'loading',
                    value: value
                });
            });
            this.eventService.registerMethod('loading.on', (value: boolean = false) => {
                if (value === true) {
                    this.eventService.useMethod('busy.on');
                }
                this.eventService.sendEvent('application', {
                    type: 'loading',
                    value: true
                });
            });
            this.eventService.registerMethod('loading.off', (value: boolean = false) => {
                if (value === true) {
                    this.eventService.useMethod('busy.off');
                }
                this.eventService.sendEvent('application', {
                    type: 'loading',
                    value: false
                });
            });
        }
    }

    protected override async doHandleApplicationEventAsync(event: any): Promise<void> {
        const eventType: string = event.type;
        switch (eventType) {
            case 'loading': {
                this.isLoading = (event.value as boolean);
                break;
            }
            default: {
                await super.doHandleApplicationEventAsync(event);
                break;
            }
        }
    }

    protected override async doSetPagesAsync(): Promise<void> {
        await super.doSetPagesAsync()

        const sidebarItems: any[] = [];
        if (this.appMode !== AppMode.Guest) {
            sidebarItems.push(...[
                {
                    items: [
                        {
                            label: 'Home',
                            icon: 'pi-fw fa fa-lg fa-home',
                            command: () => {
                                this.eventService.sendEvent(EventUI.NavigateToHome);
                            }
                        }
                    ]
                },
                {
                    label: 'Sendungen',
                    items: [
                        /*{
                            label: 'Aktuelle Sendungen',
                            icon: 'pi-fw fa fa-lg fa-mail-bulk',
                            command: () => {
                                this.eventService.sendEvent(EventUI.NavigateToShipmentList);
                            }
                        },*/
                        {
                            label: 'Sendungen zurück',
                            icon: 'pi-fw fa fa-lg fa-mail-bulk',
                            command: () => {
                                this.eventService.sendEvent(EventUI.NavigateToShipmentReturns);
                            }
                        },
                        // {
                        //     label: 'Verlorengegangene Sendungen',
                        //     icon: 'pi-fw fa fa-lg fa-mail-bulk',
                        //     command: () => {
                        //         this.eventService.sendEvent(EventUI.NavigateToShipmentLost);
                        //     }
                        // }
                    ]
                },
                {
                    label: 'Geräte',
                    items: [
                        {
                            label: 'Geräte',
                            icon: 'pi-fw fa fa-lg fa-mobile-alt',
                            command: () => {
                                this.eventService.sendEvent(EventUI.NavigateToDeviceList);
                            }
                        }
                    ]
                },
                {
                    label: 'Depots',
                    items: [
                        {
                            label: 'Verfügbare Depots',
                            icon: 'pi-fw fa fa-lg fa-warehouse',
                            command: () => {
                                this.eventService.sendEvent(EventUI.NavigateToDepotList);
                            }
                        }
                    ]
                },
                {
                    label: 'Zustelltechnik',
                    items: [
                        {
                            label: 'Zustelltechnik',
                            icon: 'pi-fw fa fa-lg fa-bicycle',
                            command: () => {
                                this.eventService.sendEvent(EventUI.NavigateToResourceList);
                            }
                        }
                    ]
                }
                // {
                //     label: 'Touren',
                //     items: [
                //         {
                //             label: 'Verfügbare Touren',
                //             icon: 'pi-fw fa fa-lg fa-warehouse',
                //             command: () => {
                //                 this.eventService.sendEvent(EventUI.NavigateToTour);
                //             }
                //         }
                //     ]
                // }
            ]);
        }
        // isDispatcher is a computed member of the DTO and does not survive the JSON round
        // trip the signed-in user takes through the session storage. The role list itself
        // does survive, so it is asked directly.
        const currentUser: IDtoUser = (this.authenticationService as IPinItTrackFrontendAuthenticationService).currentUser;
        const isDispatcher: boolean = (currentUser?.userRoles || []).some(x => (x.code === 'DISPATCHER'));

        if ((this.appMode === AppMode.Admin) ||
            (currentUser?.mandator?.code !== 'PIN') ||
            (isDispatcher === true)) {
            sidebarItems[1].items.unshift({
                label: 'Aktuelle Sendungen',
                icon: 'pi-fw fa fa-lg fa-mail-bulk',
                command: () => {
                    this.eventService.sendEvent(EventUI.NavigateToShipmentList);
                }
            });
        }
        if (this.appMode === AppMode.Admin) {
            const items: any[] = [];
            if ((this.authenticationService as IPinItTrackFrontendAuthenticationService).currentUser?.isAdmin === true) {
                items.push(
                    {
                        label: 'Koordinaten',
                        icon: 'pi-fw fa fa-lg fa-map',
                        command: () => {
                            this.eventService.sendEvent(EventUI.NavigateToLocationMap);
                        }
                    },
                );
            }
            items.push(
                {
                    label: 'Einstellungen',
                    icon: 'pi-fw fa fa-lg fa-cogs',
                    command: () => {
                        this.eventService.sendEvent(EventUI.NavigateToSettingList);
                    }
                },
                {
                    label: 'Versionen',
                    icon: 'pi-fw fa fa-lg fa-code-branch',
                    command: () => {
                        this.eventService.sendEvent(EventUI.NavigateToVersionList);
                    }
                },
                {
                    label: 'Web-Benutzer',
                    icon: 'pi-fw fa fa-lg fa-users',
                    command: () => {
                        this.eventService.sendEvent(EventUI.NavigateToWebUserList);
                    }
                },
                {
                    label: 'Geräte-Benutzer',
                    icon: 'pi-fw fa fa-lg fa-users',
                    command: () => {
                        this.eventService.sendEvent(EventUI.NavigateToDeviceUserList);
                    }
                }
            );
            sidebarItems.push(
                {
                    label: 'Administration',
                    items: items
                }
            );
        }
        setTimeout(() => {
            this.sidebarMenuItems = sidebarItems;
        }, 1000);
    }

    public breadcrumbItems$: Observable<MenuItem[]> = this.breadcrumbItemsSource.asObservable();

    public activeTopbarItem: string = null;

    public isInlineMenuActive: any = [];

    // Menu starts visible; in static mode it is not closed by clicks outside.
    public isMenuActive: boolean = true;

    public isMenuHoverActive = false;

    public isMobileMenuActive: boolean;

    public isMobileTopbarActive: boolean;

    public isRightMenuActive: boolean = false;

    public layoutMode: string = 'light';

    public menuMode: string = 'static';

    public themeMenu: string = 'bluegrey';

    public themeTopbar: string = 'bluegrey';

    public inlineMenuPosition: string = 'top';

    public inputStyle: string = 'filled';

    public menuSource$ = this.menuSource.asObservable();

    public resetSource$ = this.resetSource.asObservable();

    private isMenuClicked: boolean = false;

    private isTopbarItemClicked: boolean = false;

    private documentClickListener: () => void;

    private currentInlineMenuKey: string;

    private isInlineMenuClicked: boolean;

    public topBarMenuItems: any[] = [];

    public sidebarMenuItems: any[] = [];

    public get isDesktop(): boolean {
        return window.innerWidth > 991;
    }

    public get isMobile(): boolean {
        return window.innerWidth <= 991;
    }

    public get isSidebarOverlay(): boolean {
        return this.menuMode === 'overlay';
    }

    public get isSidebarSlim(): boolean {
        return this.menuMode === 'slim';
    }

    public get isSidebarStatic(): boolean {
        return this.menuMode === 'static';
    }

    public resetMenu() {
        this.doResetMenu();
    }

    public setBreadcrumbItems(items: MenuItem[]) {
        this.doSetBreadcrumbItems(items);
    }

    public setRenderer(document: Document, renderer: Renderer2): void {
        this.doSetRenderer(document, renderer);
    }

    public onMenuStateChange(key: string) {
        this.doOnMenuStateChange(key);
    }

    public onMenuButtonClick(event: any) {
        this.doOnMenuButtonClick(event);
    }

    public onTopbarMobileButtonClick(event: any) {
        this.doOnTopbarMobileButtonClick(event);
    }

    public onRightMenuButtonClick(event: any) {
        this.doOnRightMenuButtonClick(event);
    }

    public onMenuClick($event: any) {
        this.doOnMenuClick($event);
    }

    public onInlineMenuClick(event: any, key: any) {
        this.doOnInlineMenuClick(event, key);
    }

    public onTopbarItemClick(event: any, item: any) {
        this.doOnTopbarItemClick(event, item);
    }

    public onTopbarSubItemClick(event: any) {
        this.doOnTopbarSubItemClick(event);
    }

    protected doResetMenu() {
        this.resetSource.next();
    }

    protected doSetBreadcrumbItems(items: MenuItem[]) {
        this.breadcrumbItemsSource.next(items);
    }

    protected doSetRenderer(document: Document, renderer: Renderer2): void {
        if (this.documentClickListener) {
            this.documentClickListener();
        }
        this.document = document;
        this.renderer = renderer;
        this.documentClickListener = this.renderer.listen('body', 'click', () => {
            if (!this.isTopbarItemClicked) {
                this.activeTopbarItem = null;
            }

            if ((this.isMenuClicked === false) && (this.isSidebarSlim === true)) {
                this.resetMenu();
            }

            if (this.isMenuClicked === false) {
                if (this.isMobileMenuActive) {
                    this.isMobileMenuActive = false;
                }

                if (this.isSidebarOverlay) {
                    this.isMenuActive = false;
                }

                this.isMenuHoverActive = false;
                this.unblockBodyScroll();
            }

            if (this.isInlineMenuActive[this.currentInlineMenuKey] && !this.isInlineMenuClicked) {
                this.isInlineMenuActive[this.currentInlineMenuKey] = false;
            }

            this.isInlineMenuClicked = false;
            this.isTopbarItemClicked = false;
            this.isMenuClicked = false;
        });
    }

    protected override async handleApplicationEventInit(event: any): Promise<void> {
        if (UtilsMisc.isNullOrUndefined(event.mode) === true) {
            event.mode = AppMode.User;
        }
        this.appMode = (event.mode as AppMode);
        await super.handleApplicationEventInit(event);
    }

    protected doOnMenuStateChange(key: string) {
        this.menuSource.next(key);
    }

    protected doOnMenuButtonClick(event: any) {
        this.isMenuActive = !this.isMenuActive;
        this.isMenuClicked = true;

        if (this.isDesktop) {
        } else {
            this.isMobileMenuActive = !this.isMobileMenuActive;
            if (this.isMobileMenuActive) {
                this.blockBodyScroll();
            } else {
                this.unblockBodyScroll();
            }
        }

        event.preventDefault();
    }

    protected doOnTopbarMobileButtonClick(event: any) {
        this.isMobileTopbarActive = !this.isMobileTopbarActive;
        event.preventDefault();
    }

    protected doOnRightMenuButtonClick(event: any) {
        this.isRightMenuActive = !this.isRightMenuActive;
        event.preventDefault();
    }

    protected doOnMenuClick($event: any) {
        this.isMenuClicked = true;

        if (this.isInlineMenuActive[this.currentInlineMenuKey] && !this.isInlineMenuClicked) {
            this.isInlineMenuActive[this.currentInlineMenuKey] = false;
        }
    }

    protected doOnInlineMenuClick(event: any, key: any) {
        if (key !== this.currentInlineMenuKey) {
            this.isInlineMenuActive[this.currentInlineMenuKey] = false;
        }

        this.isInlineMenuActive[key] = !this.isInlineMenuActive[key];
        this.currentInlineMenuKey = key;
        this.isInlineMenuClicked = true;
    }

    protected doOnTopbarItemClick(event: any, item: any) {
        this.isTopbarItemClicked = true;

        if (this.activeTopbarItem === item) {
            this.activeTopbarItem = null;
        }
        else {
            this.activeTopbarItem = item;
        }

        event.preventDefault();
    }

    protected doOnTopbarSubItemClick(event: any) {
        event.preventDefault();
    }

    private blockBodyScroll(): void {
        if (this.document.body.classList) {
            this.document.body.classList.add('blocked-scroll');
        } else {
            this.document.body.className += ' blocked-scroll';
        }
    }

    private unblockBodyScroll(): void {
        if (this.document.body.classList) {
            this.document.body.classList.remove('blocked-scroll');
        } else {
            this.document.body.className = this.document.body.className.replace(new RegExp('(^|\\b)' +
                'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

}
